import { useEffect, useMemo, useState } from "react";
import { api } from "./adminApi";

export default function AdminCarousel() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  // Create form
  const [titleEn, setTitleEn] = useState("");
  const [titleKn, setTitleKn] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [sortOrder, setSortOrder] = useState(1);
  const [image, setImage] = useState(null);

  // Edit form
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editTitleEn, setEditTitleEn] = useState("");
  const [editTitleKn, setEditTitleKn] = useState("");
  const [editLinkUrl, setEditLinkUrl] = useState("");
  const [editSortOrder, setEditSortOrder] = useState(1);
  const [editImage, setEditImage] = useState(null);

  const BASE = useMemo(() => {
    // adminApi.js sets baseURL dynamically. Use it for correct preview URLs in prod too.
    return api?.defaults?.baseURL || window.location.origin;
  }, []);

  async function load() {
    setErr("");
    try {
      const res = await api.get("/api/admin/carousel");
      setRows(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load carousel");
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function createItem(e) {
    e.preventDefault();
    setErr("");
    try {
      const fd = new FormData();
      fd.append("title_en", titleEn);
      fd.append("title_kn", titleKn);
      fd.append("link_url", linkUrl);
      fd.append("sort_order", String(sortOrder));
      if (image) fd.append("image", image);

      await api.post("/api/admin/carousel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setTitleEn("");
      setTitleKn("");
      setLinkUrl("");
      setSortOrder(1);
      setImage(null);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.message || "Create failed");
    }
  }

  function startEdit(r) {
    setEditing(true);
    setEditId(r.id);
    setEditTitleEn(r.title_en || "");
    setEditTitleKn(r.title_kn || "");
    setEditLinkUrl(r.link_url || "");
    setEditSortOrder(Number(r.sort_order || 1));
    setEditImage(null);
  }

  function cancelEdit() {
    setEditing(false);
    setEditId(null);
    setEditTitleEn("");
    setEditTitleKn("");
    setEditLinkUrl("");
    setEditSortOrder(1);
    setEditImage(null);
  }

  async function saveEdit(e) {
    e.preventDefault();
    setErr("");
    if (!editId) return;

    try {
      const fd = new FormData();
      fd.append("title_en", editTitleEn);
      fd.append("title_kn", editTitleKn);
      fd.append("link_url", editLinkUrl);
      fd.append("sort_order", String(editSortOrder));
      if (editImage) fd.append("image", editImage);

      await api.put(`/api/admin/carousel/${editId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      cancelEdit();
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.message || "Update failed");
    }
  }

  async function approve(id) {
    try {
      await api.post(`/api/admin/carousel/${id}/approve`);
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || "Approve failed");
    }
  }

  async function reject(id) {
    try {
      await api.post(`/api/admin/carousel/${id}/reject`);
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || "Reject failed");
    }
  }

  async function remove(id) {
    const ok = window.confirm("Delete this carousel item?");
    if (!ok) return;

    setErr("");
    try {
      await api.delete(`/api/admin/carousel/${id}`);
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || "Delete failed");
    }
  }

  return (
    <div>
      <Top title="Carousel Management" />

      <div style={grid2}>
        <Card>
          <div style={cardTitle}>Create Carousel Item</div>
          <form onSubmit={createItem} style={{ display: "grid", gap: 10 }}>
            <input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} placeholder="Title (English)" style={inp} />
            <input value={titleKn} onChange={(e) => setTitleKn(e.target.value)} placeholder="Title (Kannada)" style={inp} />
            <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="Link URL (optional)" style={inp} />
            <input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} placeholder="Sort order" style={inp} />
            <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} style={inp} />
            <button style={btn} type="submit">Create</button>
            {err ? <div style={errStyle}>{err}</div> : null}
          </form>

          {editing ? (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eef2f6" }}>
              <div style={cardTitle}>Edit Carousel Item</div>
              <form onSubmit={saveEdit} style={{ display: "grid", gap: 10 }}>
                <input value={editTitleEn} onChange={(e) => setEditTitleEn(e.target.value)} placeholder="Title (English)" style={inp} />
                <input value={editTitleKn} onChange={(e) => setEditTitleKn(e.target.value)} placeholder="Title (Kannada)" style={inp} />
                <input value={editLinkUrl} onChange={(e) => setEditLinkUrl(e.target.value)} placeholder="Link URL (optional)" style={inp} />
                <input type="number" value={editSortOrder} onChange={(e) => setEditSortOrder(Number(e.target.value))} placeholder="Sort order" style={inp} />
                <input type="file" accept="image/*" onChange={(e) => setEditImage(e.target.files?.[0] || null)} style={inp} />

                <div style={{ display: "flex", gap: 8 }}>
                  <button style={{ ...btn, background: "#0d47a1" }} type="submit">Save</button>
                  <button style={{ ...btn, background: "#667085" }} type="button" onClick={cancelEdit}>Cancel</button>
                </div>
                <div style={{ color: "#667085", fontSize: 12 }}>
                  Note: Editing sends item back to <b>pending</b> and it must be approved again.
                </div>
              </form>
            </div>
          ) : null}
        </Card>

        <Card>
          <div style={cardTitle}>Carousel Items</div>
          <div style={{ overflowX: "auto" }}>
            <table style={tbl}>
              <thead>
                <tr>
                  <th style={th}>Preview</th>
                  <th style={th}>Title</th>
                  <th style={th}>Order</th>
                  <th style={th}>Status</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={td}>
                      {r.image_url ? (
                        <img
                          src={`${BASE}${r.image_url}`}
                          alt="carousel"
                          style={{ width: 120, height: 60, objectFit: "cover", borderRadius: 10, border: "1px solid #e7eaf0" }}
                        />
                      ) : "-"}
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 900 }}>{r.title_en || "-"}</div>
                      <div style={{ color: "#667085", fontSize: 12 }}>{r.title_kn || ""}</div>
                      {r.link_url ? (
                        <div style={{ marginTop: 6 }}>
                          <a href={r.link_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                            Open Link
                          </a>
                        </div>
                      ) : null}
                    </td>
                    <td style={td}>{r.sort_order ?? "-"}</td>
                    <td style={td}>
                      <span style={{ ...pill, background: pillBg(r.status), color: pillColor(r.status) }}>{r.status}</span>
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button style={{ ...btnAction, background: "#344054" }} onClick={() => startEdit(r)}>
                          Edit
                        </button>

                        <button style={{ ...btnAction, background: "#b42318" }} onClick={() => remove(r.id)}>
                          Delete
                        </button>

                        {r.status === "pending" ? (
                          <>
                            <button style={{ ...btnAction, background: "#027a48" }} onClick={() => approve(r.id)}>
                              Approve
                            </button>
                            <button style={{ ...btnAction, background: "#b54708" }} onClick={() => reject(r.id)}>
                              Reject
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 ? (
                  <tr>
                    <td style={td} colSpan={5}>No items.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Top({ title }) {
  return <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 12 }}>{title}</div>;
}
function Card({ children }) {
  return <div style={{ background: "white", borderRadius: 14, padding: 14, boxShadow: "0 6px 20px rgba(16,24,40,0.08)" }}>{children}</div>;
}
function pillBg(s) {
  if (s === "approved") return "#ecfdf3";
  if (s === "rejected") return "#fef3f2";
  return "#fff6ed";
}
function pillColor(s) {
  if (s === "approved") return "#027a48";
  if (s === "rejected") return "#b42318";
  return "#b54708";
}

const grid2 = { display: "grid", gridTemplateColumns: "420px 1fr", gap: 12, alignItems: "start" };
const cardTitle = { fontWeight: 900, marginBottom: 10 };
const inp = { padding: "10px 12px", borderRadius: 10, border: "1px solid #e7eaf0", outline: "none", fontSize: 14 };
const btn = { padding: "10px 12px", borderRadius: 10, border: 0, background: "#0d47a1", color: "white", cursor: "pointer", fontWeight: 900 };
const btnAction = { padding: "8px 12px", borderRadius: 10, border: 0, color: "white", cursor: "pointer", fontWeight: 900 };
const errStyle = { color: "#b42318", fontWeight: 700, marginTop: 8 };
const tbl = { width: "100%", borderCollapse: "collapse" };
const th = { textAlign: "left", padding: 10, borderBottom: "1px solid #e7eaf0", color: "#667085", fontSize: 12 };
const td = { padding: 10, borderBottom: "1px solid #f2f4f7", fontSize: 14, verticalAlign: "top" };
const pill = { display: "inline-block", padding: "4px 10px", borderRadius: 999, fontWeight: 900, fontSize: 12, textTransform: "capitalize" };
